// src/app/components/shared/underline-tabs.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// This replaces `components/ui/underline-tabs.tsx`, which existed because
// shadcn's Tabs draws a PILL and every page-level mode selector in this
// product is an UNDERLINE, so each page was overriding six to eight classes
// per trigger. Its own header said so.
//
// Kit 02 item 46 is that underline, drawn once: "the selected tab's 2px
// underline sits ON the container's 1px rule rather than below it, so
// selecting a tab does not make the row one pixel taller". The old one used a
// 4px bar at `bottom: -1px`, which is the version that does.
//
// Same props, so the two importers move by one import line each. What went:
// an inline <style> element, a hand-drawn right-edge fade, a `--alpha-teal`
// underline, and the `size` prop — item 46 has one tab height, and the two
// sizes were 4px of vertical padding apart.
import { useEffect, useRef, type ReactNode } from "react";
import { Tabs } from "../kit";
import { TabPanel, TabScope } from "./tab-panel";

export interface UnderlineTab {
  value: string;
  label: string;
  /** A small inline adornment after the label, e.g. a count. */
  labelExtra?: ReactNode;
}

export interface UnderlineTabsProps {
  tabs: UnderlineTab[];
  value: string;
  onChange: (value: string) => void;
  /** Panels. Each child should be an <UnderlineTabsPanel value="…">. */
  children: ReactNode;
  /** Kept for call-site compatibility. Item 46 has one tab height. */
  size?: "sm" | "md";
  className?: string;
  /** Kept for call-site compatibility; the row sits on the shell gutter. */
  padding?: string;
}

export function UnderlineTabs({
  tabs, value, onChange, children, className = "",
}: UnderlineTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll the selected tab into view on mount and on change. On a narrow
  // viewport the default-selected tab sits at the left edge while later tabs
  // clip off the right, and without this nudge there is no affordance that
  // more tabs exist.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const raf = requestAnimationFrame(() => {
      const activeEl = container.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!activeEl) return;
      const cRect = container.getBoundingClientRect();
      const aRect = activeEl.getBoundingClientRect();
      if (aRect.left >= cRect.left && aRect.right <= cRect.right) return;
      activeEl.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    });
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <TabScope value={value}>
      <div className={`upm-tabs ${className}`}>
      <div className="upm-tabs__primary">
        <div ref={scrollRef} className="upm-tabs__scroll">
          <Tabs
            label="Sections"
            items={tabs.map((t) => ({ value: t.value, label: t.label }))}
            value={value}
            onChange={onChange}
          />
        </div>
      </div>

      {/* One panel renders. The others are not in the DOM, which is what a
          tab panel means and what shadcn's TabsContent did too. */}
      {children}
      </div>
    </TabScope>
  );
}

/** The selected panel's content. Reads the selection from `TabScope`. */
export function UnderlineTabsPanel({
  value, children, className = "",
}: { value: string; children: ReactNode; className?: string }) {
  return <TabPanel value={value} className={className}>{children}</TabPanel>;
}
